import { ApiError } from "./errors";

export const AUTHENTICATION_REQUIRED_EVENT = "mbip:authentication-required";

interface ApiClientConfiguration {
  baseUrl: string;
  timeoutMs: number;
  fetcher?: typeof fetch;
}

export interface ApiRequestOptions extends Omit<RequestInit, "signal"> {
  signal?: AbortSignal;
  timeoutMs?: number;
}

export interface ApiDownload {
  blob: Blob;
  filename: string | null;
  generationSeconds: string | null;
}

interface BackendErrorPayload {
  code?: unknown;
  detail?: unknown;
  message?: unknown;
  error?: unknown;
}

function errorPayload(value: unknown): BackendErrorPayload {
  if (typeof value !== "object" || value === null) return {};
  const payload = value as BackendErrorPayload;
  return typeof payload.error === "object" && payload.error !== null
    ? (payload.error as BackendErrorPayload)
    : payload;
}

function readableMessage(payload: BackendErrorPayload, fallback: string): string {
  if (typeof payload.detail === "string" && payload.detail.trim() !== "") {
    return payload.detail;
  }
  if (typeof payload.message === "string" && payload.message.trim() !== "") {
    return payload.message;
  }
  return fallback;
}

export function csrfTokenFromCookie(cookieHeader: string): string | undefined {
  const prefix = "mbip_csrf=";
  const value = cookieHeader
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
    ?.slice(prefix.length);
  return value === undefined ? undefined : decodeURIComponent(value);
}

function requestHeaders(options: ApiRequestOptions): Headers {
  const headers = new Headers(options.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  const method = (options.method ?? "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method) && !headers.has("X-MBIP-CSRF")) {
    const token = typeof document === "undefined"
      ? undefined
      : csrfTokenFromCookie(document.cookie);
    if (token !== undefined && token !== "") headers.set("X-MBIP-CSRF", token);
  }
  return headers;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly defaultTimeoutMs: number;
  private readonly fetcher: typeof fetch;
  private readonly inFlightGets = new Map<string, Promise<unknown>>();

  constructor(configuration: ApiClientConfiguration) {
    this.baseUrl = configuration.baseUrl.replace(/\/$/, "");
    this.defaultTimeoutMs = configuration.timeoutMs;
    this.fetcher = configuration.fetcher ?? globalThis.fetch.bind(globalThis);
  }

  get<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    if (options.signal !== undefined) {
      return this.request<T>(path, { ...options, method: "GET" });
    }

    const key = `${this.baseUrl}${path}`;
    const existing = this.inFlightGets.get(key) as Promise<T> | undefined;
    if (existing !== undefined) {
      return existing;
    }

    const request = this.request<T>(path, { ...options, method: "GET" }).finally(
      () => this.inFlightGets.delete(key),
    );
    this.inFlightGets.set(key, request);
    return request;
  }

  async request<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    const timeoutMs = options.timeoutMs ?? this.defaultTimeoutMs;
    const controller = new AbortController();
    let timedOut = false;
    const abortFromCaller = () => controller.abort(options.signal?.reason);

    if (options.signal?.aborted) {
      throw new ApiError({ kind: "cancelled", message: "Request cancelled." });
    }
    options.signal?.addEventListener("abort", abortFromCaller, { once: true });
    const timeout = globalThis.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    try {
      const response = await this.fetcher(`${this.baseUrl}${path}`, {
        ...options,
        credentials: options.credentials ?? "include",
        headers: requestHeaders(options),
        signal: controller.signal,
      });
      const correlationId = response.headers.get("x-correlation-id") ?? undefined;
      const rawText = response.status === 204 ? "" : await response.text();
      let payload: unknown;

      if (rawText !== "") {
        try {
          payload = JSON.parse(rawText) as unknown;
        } catch (cause) {
          throw new ApiError({
            kind: "parse",
            message: "Backend returned an invalid JSON response.",
            status: response.status,
            correlationId,
            cause,
          });
        }
      }

      if (!response.ok) {
        const backendError = errorPayload(payload);
        notifyAuthenticationRequired(response.status, path);
        throw new ApiError({
          kind: "http",
          message: readableMessage(
            backendError,
            `Backend request failed with HTTP ${response.status}.`,
          ),
          status: response.status,
          code:
            typeof backendError.code === "string" ? backendError.code : undefined,
          correlationId,
          details: payload,
        });
      }

      return payload as T;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (controller.signal.aborted) {
        throw new ApiError({
          kind: timedOut ? "timeout" : "cancelled",
          message: timedOut
            ? `Backend request timed out after ${timeoutMs} ms.`
            : "Request cancelled.",
          cause: error,
        });
      }
      throw new ApiError({
        kind: "network",
        message: "Backend could not be reached.",
        cause: error,
      });
    } finally {
      globalThis.clearTimeout(timeout);
      options.signal?.removeEventListener("abort", abortFromCaller);
    }
  }

  async download(path: string, options: ApiRequestOptions = {}): Promise<ApiDownload> {
    const timeoutMs = options.timeoutMs ?? this.defaultTimeoutMs;
    const controller = new AbortController();
    let timedOut = false;
    const abortFromCaller = () => controller.abort(options.signal?.reason);

    if (options.signal?.aborted) {
      throw new ApiError({ kind: "cancelled", message: "Request cancelled." });
    }
    options.signal?.addEventListener("abort", abortFromCaller, { once: true });
    const timeout = globalThis.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    try {
      const response = await this.fetcher(`${this.baseUrl}${path}`, {
        ...options,
        credentials: options.credentials ?? "include",
        signal: controller.signal,
      });
      const correlationId = response.headers.get("x-correlation-id") ?? undefined;
      if (!response.ok) {
        const rawText = await response.text();
        let payload: unknown = {};
        try { payload = rawText ? JSON.parse(rawText) : {}; } catch { payload = {}; }
        const backendError = errorPayload(payload);
        notifyAuthenticationRequired(response.status, path);
        throw new ApiError({
          kind: "http",
          message: readableMessage(backendError, `Backend request failed with HTTP ${response.status}.`),
          status: response.status,
          code: typeof backendError.code === "string" ? backendError.code : undefined,
          correlationId,
          details: payload,
        });
      }
      return {
        blob: await response.blob(),
        filename: downloadFilename(response.headers.get("content-disposition")),
        generationSeconds: response.headers.get("x-export-generation-seconds"),
      };
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (controller.signal.aborted) {
        throw new ApiError({
          kind: timedOut ? "timeout" : "cancelled",
          message: timedOut
            ? `Backend request timed out after ${timeoutMs} ms.`
            : "Request cancelled.",
          cause: error,
        });
      }
      throw new ApiError({ kind: "network", message: "Backend could not be reached.", cause: error });
    } finally {
      globalThis.clearTimeout(timeout);
      options.signal?.removeEventListener("abort", abortFromCaller);
    }
  }
}

function notifyAuthenticationRequired(status: number, path: string): void {
  if (
    status === 401 &&
    !["/api/auth/login", "/api/auth/me"].includes(path) &&
    typeof window !== "undefined"
  ) {
    window.dispatchEvent(new Event(AUTHENTICATION_REQUIRED_EVENT));
  }
}

function downloadFilename(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return match?.[1] ?? null;
}
